import React, { useState, useEffect } from "react";
import { getProfile } from "@services/krent.service";
import { validateCurrentToken, clearAuthData } from "@utils/tokenUtils";
import { shouldShowGuestProfileForm } from "@utils/guestProfileForm";
import { LoginModal } from "../../LoginModal";

interface BookingFormProps {
  onValidationChange?: (isValid: boolean) => void;
  onGuestDataChange?: (formData: any) => void;
  onVisibilityChange?: (isVisible: boolean) => void;
  onTermsChange?: (accepted: boolean) => void;
  /** Passed from navigation state to avoid profile-section flash */
  initialShowForm?: boolean;
}

const BookingForm: React.FC<BookingFormProps> = ({
  onValidationChange,
  onGuestDataChange,
  onVisibilityChange,
  onTermsChange,
  initialShowForm,
}) => {
  const [user, setUser] = useState<any>({});
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showForm, setShowForm] = useState(
    typeof initialShowForm === "boolean" ? initialShowForm : true,
  );
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    whatsapp: "",
    specialRequests: "",
    termsAccepted: false,
  });

  const handleSignIn = () => {
    setShowLoginModal(true);
  };

  const applyVisibility = (visible: boolean) => {
    setShowForm(visible);
    onVisibilityChange?.(visible);
  };

  const fetchUser = async () => {
    const token = validateCurrentToken();

    if (token) {
      try {
        const profile = await getProfile(new AbortController().signal);

        const profileResponse = profile as any;
        const userData = profileResponse.data || profileResponse;
        setUser(userData);

        if (userData) {
          const phone = (userData as any).phone || "";
          const whatsapp = (userData as any).whatsappNumber || "";

          setFormData((prev) => ({
            ...prev,
            firstName: (userData as any).firstname || "",
            lastName: (userData as any).lastname || "",
            email: (userData as any).email || "",
            phone: phone,
            whatsapp: whatsapp,
          }));

          // Only decide visibility here when it wasn't passed from navigation
          if (typeof initialShowForm !== "boolean") {
            applyVisibility(shouldShowGuestProfileForm(userData));
          }
        }
      } catch (error) {
        clearAuthData();
        setUser({});
        if (typeof initialShowForm !== "boolean") {
          applyVisibility(true);
        }
      }
    } else {
      setUser({});
      if (typeof initialShowForm !== "boolean") {
        applyVisibility(true);
      }
    }
  };

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    whatsapp: "",
  });

  useEffect(() => {
    if (onValidationChange) {
      const newErrors = {
        firstName: formData.firstName.trim() ? "" : "First name is required",
        lastName: formData.lastName.trim() ? "" : "Last name is required",
        email:
          formData.email.trim() &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
            ? ""
            : "Valid email is required",
        phone: formData.phone.trim() ? "" : "Phone number is required",
        whatsapp: formData.whatsapp.trim() ? "" : "Whatsapp number is required",
      };

      setErrors(newErrors);

      const isValid =
        Object.values(newErrors).every((error) => error === "") &&
        formData.termsAccepted;

      onValidationChange(isValid);
    }
  }, [formData, onValidationChange]);

  const handleInputChange = (field: string, value: string | boolean) => {
    const newFormData = {
      ...formData,
      [field]: value,
    };
    setFormData(newFormData);

    if (onGuestDataChange) {
      onGuestDataChange(newFormData);
    }

    // Call onTermsChange when termsAccepted changes
    if (field === "termsAccepted" && onTermsChange) {
      onTermsChange(value as boolean);
    }
  };

  useEffect(() => {
    if (typeof initialShowForm === "boolean") {
      applyVisibility(initialShowForm);
    }
  }, [initialShowForm]);

  useEffect(() => {
    fetchUser();
  }, []);

  // Send guest data when form is hidden
  useEffect(() => {
    if (!showForm && onGuestDataChange) {
      onGuestDataChange({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        whatsapp: formData.whatsapp,
        specialRequests: formData.specialRequests,
        termsAccepted: formData.termsAccepted,
      });
    }
  }, [showForm, formData, onGuestDataChange]);

  // Return null if form should not be shown
  if (!showForm) {
    return null;
  }

  return (
    <div className=" py-2">
      <div className="mx-auto" style={{ maxWidth: "900px" }}>
        <div className="pb-3">
          <h3 className="fw-semibold fs-24 font-onest">Request to book</h3>
          <p className="text-muted font-onest">
            Please provide your details to continue with the booking
          </p>
        </div>

        {!user?.id && (
          <div
            className="border rounded-4 p-3 d-flex justify-content-between align-items-center mb-4"
            style={{ borderColor: "#BEDBFF", backgroundColor: "#EFF6FF" }}
          >
            <div>
              <div className="fw-semibold font-onest">
                Already have an account?
              </div>
              <small className="text-muted font-onest">
                Sign in to auto-fill your details
              </small>
            </div>
            <button
              className="btn btn-light border rounded-3 font-onest fs-14"
              onClick={handleSignIn}
            >
              Sign in
            </button>
          </div>
        )}

        <div className="border rounded-4 p-4">
          <h5 className="fw-bold mb-4 font-onest">1. Your details</h5>

          <form>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label font-onest">First name*</label>
                <input
                  type="text"
                  className={`form-control font-onest ${errors.firstName ? "is-invalid" : ""}`}
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                />
                {errors.firstName && (
                  <div className="invalid-feedback font-onest">
                    {errors.firstName}
                  </div>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label font-onest">Last name*</label>
                <input
                  type="text"
                  className={`form-control font-onest ${errors.lastName ? "is-invalid" : ""}`}
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                />
                {errors.lastName && (
                  <div className="invalid-feedback font-onest">
                    {errors.lastName}
                  </div>
                )}
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label font-onest">Email*</label>
              <input
                type="email"
                className={`form-control font-onest ${errors.email ? "is-invalid" : ""}`}
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
              />
              {errors.email && (
                <div className="invalid-feedback font-onest">
                  {errors.email}
                </div>
              )}
              <small className="text-muted font-onest">
                We'll send your booking confirmation here
              </small>
            </div>

            <div className="mb-3">
              <label className="form-label font-onest">Phone number*</label>
              <input
                type="text"
                className={`form-control font-onest ${errors.phone ? "is-invalid" : ""}`}
                placeholder="+234 8134242323"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
              />
              {errors.phone && (
                <div className="invalid-feedback font-onest">
                  {errors.phone}
                </div>
              )}
              <small className="text-muted font-onest">
                For booking updates and questions
              </small>
            </div>

            <div className="mb-3">
              <label className="form-label font-onest">Whatsapp number*</label>
              <input
                type="text"
                className={`form-control font-onest ${errors.whatsapp ? "is-invalid" : ""}`}
                placeholder="+234 9134249999"
                value={formData.whatsapp}
                onChange={(e) => handleInputChange("whatsapp", e.target.value)}
              />
              {errors.whatsapp && (
                <div className="invalid-feedback font-onest">
                  {errors.whatsapp}
                </div>
              )}
              <small className="text-muted font-onest">
                For booking updates and questions
              </small>
            </div>

            <div className="mb-4">
              <label className="form-label font-onest">
                Special requests (optional)
              </label>
              <textarea
                className="form-control font-onest"
                rows={4}
                placeholder="Any special requests or requirements?"
                value={formData.specialRequests}
                onChange={(e) =>
                  handleInputChange("specialRequests", e.target.value)
                }
              ></textarea>
            </div>

            <div className="border rounded p-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="terms"
                  checked={formData.termsAccepted}
                  onChange={(e) =>
                    handleInputChange("termsAccepted", e.target.checked)
                  }
                />
                <label className="form-check-label font-onest" htmlFor="terms">
                  I agree to the{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none text-primary fw-semibold"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms & Conditions
                  </a>{" "}
                  and{" "}
                  <a
                    href="/cancellation-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none text-primary fw-semibold"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Cancellation Policy
                  </a>
                </label>
              </div>
              {!formData.termsAccepted && (
                <small className="text-danger font-onest mt-2 d-block">
                  You must accept the terms and conditions to continue
                </small>
              )}
            </div>
          </form>
        </div>
      </div>
      <LoginModal
        show={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onOpenSignup={() => {
          // Optional: You can implement signup modal opening here if needed
          setShowLoginModal(false);
        }}
        disableNavigation={true}
        onLoginSuccess={fetchUser}
      />
    </div>
  );
};

export default BookingForm;
